import { useEffect, useMemo, useRef, useState } from 'react'

import { createConsumer } from '@rails/actioncable'
import axios from 'axios'

import { buildRealtimeUrl, buildUrl } from 'modules/core/lib/buildUrl'

// Types for transcript channel connection
interface TranscriptChannelConnection {
  isConnected: boolean
  perform?: (action: string, data: Record<string, unknown>) => void
}

// Type for transcript segment data
interface TranscriptSegment {
  type: string
  // Add other properties as needed
}

// Function to get JWT token for AnyCable authentication - matching buildClient.ts pattern
async function getRealtimeToken(): Promise<string | null> {
  try {
    const { data } = await axios({
      method: 'POST',
      url: buildUrl('/authentication/realtime/token'),
      withCredentials: true,
    })

    return data.token
  } catch (error) {
    return null
  }
}

export function useTranscriptChannel(
  meetingId: number,
  roomName: string,
  subscriptionRef: React.MutableRefObject<{
    perform?: (action: string, data: Record<string, unknown>) => void
    unsubscribe?: () => void
  } | null>
): TranscriptChannelConnection & {
  connectionError: string | null
  isAuthenticating: boolean
} {
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const consumerRef = useRef<ReturnType<typeof createConsumer> | null>(null)
  const [isMounted, setIsMounted] = useState(true)

  useEffect(() => {
    async function connectWithJWT() {
      if (!meetingId || !roomName) {
        return
      }

      // Get JWT token for AnyCable authentication - matching buildClient.ts pattern
      const token = await getRealtimeToken()

      if (!token) {
        if (isMounted) {
          setConnectionError('Failed to obtain authentication token')
          setIsConnected(false)
          setIsAuthenticating(false)
        }
        return
      }

      if (!isMounted) return

      // Create the ActionCable consumer with JWT token - matching buildClient.ts pattern
      const consumer = createConsumer(buildRealtimeUrl(`/cable?token=${token}`))
      consumerRef.current = consumer

      const subscription = consumer.subscriptions.create(
        {
          channel: 'TranscriptChannel',
          room_name: roomName,
          meeting_id: meetingId,
        },
        {
          connected() {
            // Store the subscription in the provided ref
            subscriptionRef.current = subscription

            if (isMounted) {
              setIsConnected(true)
              setConnectionError(null)
              setIsAuthenticating(false)
            }
          },

          disconnected() {
            if (isMounted) {
              setIsConnected(false)
              setIsAuthenticating(false)
            }
          },

          rejected() {
            if (isMounted) {
              setIsConnected(false)
              setConnectionError('Connection rejected - insufficient permissions')
              setIsAuthenticating(false)
            }
          },

          received(data: TranscriptSegment) {
            // Handle incoming transcript segments
            if (data.type === 'transcript_segment') {
              // Future: handle transcript segments in UI
              // For now, we'll store the data for future processing
            }
          },
        }
      )
    }

    connectWithJWT()

    // Cleanup on unmount
    return () => {
      setIsMounted(false)
      if (subscriptionRef.current?.unsubscribe) {
        subscriptionRef.current.unsubscribe()
      }
      if (consumerRef.current?.disconnect) {
        consumerRef.current.disconnect()
      }
    }
  }, [meetingId, roomName, subscriptionRef, isMounted])

  return useMemo(
    () => ({
      isConnected,
      connectionError,
      isAuthenticating,
      perform: subscriptionRef.current?.perform
        ? (action: string, data: Record<string, unknown>) => {
            // Call perform on the subscription object to maintain proper context
            return subscriptionRef.current?.perform?.(action, data)
          }
        : undefined,
    }),
    [isConnected, connectionError, isAuthenticating, subscriptionRef]
  )
}
